import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import useTitle from "@/lib/useTitle";
import brand from "@/config/brand";

const Portfolio = () => {
  useTitle(
    "Portfolio",
    "Real websites for real businesses — explore home-services websites, interactive tools, and wellness brands created by The Web Whisperer."
  );

  return (
    <main data-testid="portfolio-page" className="bg-cream pt-[72px]">
      {/* Header */}
      <section className="relative overflow-hidden py-20 md:py-28">
        <div className="noise-overlay" aria-hidden="true" />
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-champagne/30 blur-[140px] pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-3xl">
            <p className="text-xs font-sans uppercase tracking-[0.28em] font-semibold text-turquoise-deep mb-6 flex items-center gap-3">
              <span className="w-10 h-px bg-turquoise inline-block" />
              Our Work
            </p>
            <h1 className="font-serif font-medium tracking-tight text-charcoal text-4xl sm:text-5xl lg:text-6xl leading-[1.08]">
              Real Websites. <span className="italic text-turquoise">Real Businesses.</span>
            </h1>
            <p className="mt-6 text-lg font-sans text-taupe leading-relaxed max-w-2xl">
              From beautiful business websites to interactive tools, explore websites and digital
              experiences we've created.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Projects */}
      <section className="pb-20 md:pb-28 space-y-16 md:space-y-24">
        {brand.portfolio.map((p, i) => (
          <Reveal key={p.id}>
            <article
              data-testid={`portfolio-detail-${p.id}`}
              className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
            >
              <div
                className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center ${
                  i % 2 === 1 ? "" : ""
                }`}
              >
                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Visit ${p.title} website`}
                  className={`group lg:col-span-7 block rounded-2xl overflow-hidden border border-taupe/15 shadow-[0_24px_48px_-16px_rgba(32,32,32,0.15)] hover:border-turquoise/50 transition-[border-color,box-shadow] duration-300 ${
                    i % 2 === 1 ? "lg:order-2" : ""
                  }`}
                >
                  <div className="overflow-hidden aspect-[16/10]">
                    <img
                      src={p.image}
                      alt={`${p.title} website screenshot`}
                      loading="lazy"
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
                    />
                  </div>
                </a>
                <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                  <p className="text-[11px] font-sans uppercase tracking-[0.18em] text-turquoise-deep font-semibold">
                    {p.category}
                  </p>
                  <h2 className="mt-2 font-serif font-medium text-2xl sm:text-3xl text-charcoal leading-snug">
                    {p.title}
                  </h2>
                  <p className="mt-4 font-sans text-base text-taupe leading-relaxed">{p.description}</p>
                  {p.features && (
                    <ul className="mt-6 space-y-2.5">
                      {p.features.map((f) => (
                        <li key={f} className="flex items-start gap-3 font-sans text-sm text-charcoal/80">
                          <Check size={15} className="text-turquoise-deep shrink-0 mt-0.5" strokeWidth={2.5} />
                          {f}
                        </li>
                      ))}
                    </ul>
                  )}
                  <a
                    data-testid={`portfolio-detail-view-${p.id}`}
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group/link mt-8 inline-flex items-center gap-2 border-2 border-charcoal text-charcoal hover:bg-charcoal hover:text-cream px-7 py-3 rounded-full font-sans text-sm font-medium transition-colors duration-300"
                  >
                    View Website
                    <ArrowUpRight size={16} className="transition-transform duration-300 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
                  </a>
                </div>
              </div>
            </article>
          </Reveal>
        ))}
      </section>

      {/* CTA band */}
      <section data-testid="portfolio-cta-band" className="relative bg-charcoal py-20 md:py-28 overflow-hidden">
        <div className="noise-overlay" aria-hidden="true" />
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-turquoise/10 blur-[150px] pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-serif font-medium tracking-tight text-cream text-2xl sm:text-3xl lg:text-4xl leading-tight">
              Want a website like <span className="italic text-turquoise">these?</span>
            </h2>
            <p className="mt-5 font-sans text-base text-champagne/60 leading-relaxed max-w-xl mx-auto">
              Tell us about your business and we'll create something you'll be proud to share.
            </p>
            <Link
              data-testid="portfolio-page-quote-button"
              to="/contact"
              className="group mt-10 inline-flex items-center gap-2 bg-turquoise text-charcoal hover:bg-turquoise-dark px-10 py-4 rounded-full font-sans font-semibold text-base shadow-[0_20px_40px_-12px_rgba(0,175,163,0.5)] transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5"
            >
              Request a Quote
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Portfolio;
